import React, { useEffect, useMemo } from 'react';

import { SynchronizeArrowsWarning } from 'src/app/components/icons/synchronize-arrows-warning';
import { AlertBanner, AlertBannerStyles } from 'src/app/components/lib/alert-banner';
import { WizardCardList } from 'src/app/components/lib/wizard/card';

import { useSyncDisabledStatus } from '../../shared/use-sync-disabled-status';
import { useDiscountWizardStateContext } from '../../state';
import { ApplicationMethodTypeId } from '../../types';

import { ConfigureCard } from './configure-card';
import { DetailsCard } from './details-card';
import { OnlineDetailsCard } from './online-details-card';
import { RequirementsCard } from './requirements-card';
import { RewardCard } from './reward-card';
import { Heading, Wrapper } from './styled';

import type { WizardStepComponentProps } from 'src/app/components/lib/wizard';

export function Summary({
  navigateToDeadEnd,
  stepStatusMap,
  disableStepByIndex,
  enableStepByIndex,
}: WizardStepComponentProps) {
  const onEditClickHandlers = useMemo(
    () => ({
      configure: () => navigateToDeadEnd({ destinationStep: 0, sourceStep: 5 }),
      details: () => navigateToDeadEnd({ destinationStep: 1, sourceStep: 5 }),
      requirements: () => navigateToDeadEnd({ destinationStep: 2, sourceStep: 5 }),
      reward: () => navigateToDeadEnd({ destinationStep: 3, sourceStep: 5 }),
      onlineDetails: () => navigateToDeadEnd({ destinationStep: 4, sourceStep: 5 }),
    }),
    [navigateToDeadEnd]
  );

  const { configureStepState, isManualDiscount } = useDiscountWizardStateContext();
  const { applicationMethod } = configureStepState;

  const { syncDisabled, syncDisabledWarningText } = useSyncDisabledStatus();

  const isOnlineDetailsStepDisabled = stepStatusMap[4].disabled;

  useEffect(() => {
    if (
      isManualDiscount ||
      applicationMethod === ApplicationMethodTypeId.DutchieLoyalty ||
      applicationMethod === ApplicationMethodTypeId.ExternalLoyalty
    ) {
      disableStepByIndex(4);
    } else {
      enableStepByIndex(4);
    }
  }, [applicationMethod, disableStepByIndex, enableStepByIndex, isManualDiscount]);

  return (
    <Wrapper>
      {syncDisabled && (
        <AlertBanner
          hasBorder
          icon={<SynchronizeArrowsWarning color='var(--color-yellow-100)' />}
          marginBottom='var(--sizes-70)'
          marginTop='0'
          style={AlertBannerStyles.warning}
          text={syncDisabledWarningText}
          title='This discount will not be shown online in your Dutchie Ecommerce store due to the following selections:'
          width='752px'
        />
      )}
      <Heading>Summary</Heading>
      <WizardCardList $maxWidth='752px'>
        <ConfigureCard onEditClick={onEditClickHandlers.configure} />
        <DetailsCard onEditClick={onEditClickHandlers.details} />
        <RequirementsCard onEditClick={onEditClickHandlers.requirements} />
        <RewardCard onEditClick={onEditClickHandlers.reward} />
        {!isOnlineDetailsStepDisabled && <OnlineDetailsCard onEditClick={onEditClickHandlers.onlineDetails} />}
      </WizardCardList>
    </Wrapper>
  );
}
